import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Inbox, Plus, Copy, Trash2, Loader2, Link2 } from "lucide-react";
import PageWrap from "@/fileshare/components/PageWrap";
import api from "@/fileshare/lib/api";
import { formatApiErrorDetail, formatDate } from "@/fileshare/lib/format";
import { copyToClipboard } from "@/fileshare/lib/clipboard";
import { Button } from "@/fileshare/components/ui/button";
import { Input } from "@/fileshare/components/ui/input";
import { Label } from "@/fileshare/components/ui/label";
import { Textarea } from "@/fileshare/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/fileshare/components/ui/dialog";

export default function FileRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/file-requests/my-requests");
      setRequests(data.requests);
    } catch (e) {
      toast.error("Failed to load requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    setCreating(true);
    try {
      await api.post("/file-requests/create", { title, description });
      toast.success("File request created");
      setOpen(false); setTitle(""); setDescription("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async (slug) => {
    const ok = await copyToClipboard(`${window.location.origin}/fileshares/r/${slug}`);
    if (ok) toast.success("Request link copied");
    else toast.error("Could not copy — copy the link manually");
  };

  const del = async (id) => {
    try {
      await api.delete(`/file-requests/${id}`);
      toast.success("Request deactivated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <PageWrap className="max-w-5xl mx-auto px-6 pt-12 pb-24">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">File requests</h1>
          <p className="text-sm text-slate-500 mt-1">Create links that let others upload files directly to your account.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-request-btn" className="bg-blue-500 hover:bg-blue-600 text-white gap-2"><Plus className="h-4 w-4" /> New request</Button>
          </DialogTrigger>
          <DialogContent data-testid="request-dialog">
            <DialogHeader><DialogTitle>Create file request</DialogTitle>
              <DialogDescription>Generate a link others can use to upload files directly to your account.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <Label className="text-sm text-slate-700">Title</Label>
                <Input data-testid="request-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Send me your documents" className="mt-1.5 bg-slate-50/60 border-slate-200" />
              </div>
              <div>
                <Label className="text-sm text-slate-700">Description (optional)</Label>
                <Textarea data-testid="request-description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Please upload the signed contract." className="mt-1.5 bg-slate-50/60 border-slate-200" />
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="create-request-submit" onClick={create} disabled={creating} className="bg-blue-500 hover:bg-blue-600 text-white">
                {creating ? "Creating…" : "Create request"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 text-blue-500 animate-spin" /></div>
      ) : requests.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
          <Inbox className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No file requests yet. Create one to start collecting files.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {requests.map((r, i) => (
            <motion.div
              key={r.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="bg-white border border-slate-200 rounded-lg p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
              data-testid={`request-card-${r.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="h-9 w-9 rounded-md bg-blue-50 flex items-center justify-center"><Inbox className="h-4 w-4 text-blue-500" /></div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{r.file_count} files</span>
              </div>
              <h3 className="mt-3 text-base font-semibold text-slate-900">{r.title}</h3>
              {r.description && <p className="text-sm text-slate-500 mt-1 line-clamp-2">{r.description}</p>}
              <p className="text-xs text-slate-400 mt-2 flex items-center gap-1"><Link2 className="h-3 w-3" /> /r/{r.request_slug}</p>
              <div className="mt-4 flex gap-2">
                <Button data-testid={`copy-request-${r.id}`} onClick={() => copyLink(r.request_slug)} variant="outline" size="sm" className="border-slate-200 text-slate-700 gap-1.5 flex-1"><Copy className="h-3.5 w-3.5" /> Copy link</Button>
                <Button data-testid={`delete-request-${r.id}`} onClick={() => del(r.id)} variant="ghost" size="icon" className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </PageWrap>
  );
}
